import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosGet, axiosPatch, axiosPost, axiosDeleteWithBody, axiosDelete } from 'Redux/axiosHelper'
import {
  ClassesDiscountCodeResponse,
  ClassesDiscountCodeApiResponse,
  CreateClassesDiscountCode,
  UpdateStep6LateFeeBody,
  UpdateStep6TicketStatusBody,
  ClassesLateFee,
  Step6DropInTicket,
  DropInTicketStatus,
} from 'types/Classes/Classes'
import { AdmissionTicketPayloadTypes, TicketColors, DiscountCreateProps, DiscountResp } from 'types/event/eventStep4'

// ticket colors (shared list endpoint)
const getTicketColors = async () => {
  const response = await axiosGet(`v1/lists/ticket-colors`)
  return response.data
}

export const useGetTicketColors = () => {
  return useQuery<TicketColors[]>(['ticket-colors'], getTicketColors, {
    enabled: false,
  })
}

// step 6 - admission ticket (create/update)
const editClassesStep6AdmissionTicket = async ({
  body,
  classId,
  ticketId,
}: {
  body: AdmissionTicketPayloadTypes
  classId: string
  ticketId?: string
}) => {
  const response = await axiosPatch(
    body,
    ticketId
      ? `v1/edu-class/${classId}/step6-admission-ticket?ticketId=${ticketId}`
      : `v1/edu-class/${classId}/step6-admission-ticket`,
  )
  return response.data
}

export const useEditStep6AdmissionTicketMutation = () => {
  return useMutation(editClassesStep6AdmissionTicket)
}

// step 6 - additional options (create/update)
const editClassesStep6AdditionalOption = async ({
  body,
  classId,
  ticketId,
}: {
  body: AdmissionTicketPayloadTypes
  classId: string
  ticketId?: string
}) => {
  const response = await axiosPatch(
    body,
    ticketId
      ? `v1/edu-class/${classId}/step6-additional-option?ticketId=${ticketId}`
      : `v1/edu-class/${classId}/step6-additional-option`,
  )
  return response.data
}

export const useEditStep6AdditionalOptionsMutation = () => {
  return useMutation(editClassesStep6AdditionalOption)
}

// step 6 - late fee
const editClassesStep6LateFee = async ({
  body,
  classId,
}: {
  body: UpdateStep6LateFeeBody
  classId: string
}) => {
  const response = await axiosPatch(body, `v1/edu-class/${classId}/step6-late-fee`)
  return response.data
}

export const useEditStep6LateFeeMutation = () => {
  return useMutation(editClassesStep6LateFee)
}

// step 6 - ticket status
const editClassesStep6TicketStatus = async ({
  body,
  classId,
}: {
  body: UpdateStep6TicketStatusBody
  classId: string
}) => {
  const response = await axiosPatch(
    body,
    `v1/edu-class/${classId}/step6-ticket-status`,
    `Ticket successfully ${body.status}`,
  )
  return response.data
}

export const useEditStep6TicketStatusMutation = () => {
  return useMutation(editClassesStep6TicketStatus)
}


const createClassesDiscountCode = async ({
  payload,
  classId,
}: {
  payload: CreateClassesDiscountCode
  classId: string
}) => {
  const response = await axiosPatch(
    payload,
    `v1/edu-class/${classId}/create-discount-code`
  )
  return response.data
}

export const useCreateClassesDiscountCode = () => {
  const queryClient = useQueryClient()

  return useMutation(createClassesDiscountCode, {
    onSuccess: (discount) => {
      queryClient.setQueryData(
        ['class-discount-code'],
        (oldData: ClassesDiscountCodeApiResponse | undefined) => {
          const existingCodes =
            oldData?.discountCodes && Array.isArray(oldData.discountCodes)
              ? oldData.discountCodes
              : []

          return {
            ...oldData,
            discountCodes: [...existingCodes, discount],
          }
        }
      )
    },
  })
}
// patch request update discount code
const updateClassesDiscountCode = async ({
  payload,
  discountCodeId,
}: {
  payload: CreateClassesDiscountCode
  discountCodeId: string
}) => {
  const response = await axiosPatch(payload, `v1/edu-class/${discountCodeId}/update-discount-code`)
  return response.data
}

export const useUpdateClassesDiscountCode = () => {
  const queryClient = useQueryClient()
  return useMutation(updateClassesDiscountCode, {
    onSuccess: discount => {
      queryClient.setQueryData(
        ['class-discount-code'],
        (oldData: ClassesDiscountCodeApiResponse | undefined) => {
          // Handle different possible data structures
          let existingCodes = []
          if (oldData && Array.isArray(oldData)) {
            // If oldData is directly an array (the actual API response)
            existingCodes = oldData
          } else if (oldData && oldData.discountCodes && Array.isArray(oldData.discountCodes)) {
            // If oldData has discountCodes property
            existingCodes = oldData.discountCodes
          }
          
          // Update the discount code
          const updatedCodes = existingCodes.map(data =>
            data._id === discount._id ? discount : data,
          )
          
          // Return the correct structure
          const newCacheData: ClassesDiscountCodeApiResponse = {
            discountCodes: updatedCodes,
            class: {}
          }
          return newCacheData
        },
      )
    },
  })
}

// patch request update discount code status
const updateClassesDiscountStatus = async ({
  payload,
  discountCodeId,
}: {
  payload: { status: string }
  discountCodeId: string
}) => {
  const response = await axiosPatch(
    payload,
    `v1/edu-class/discount-code/${discountCodeId}/status`,
  )
  return response.data
}

export const useUpdateClassesDiscountStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(updateClassesDiscountStatus, {
    onSuccess: discount => {
      queryClient.setQueryData(
        ['class-discount-code'],
        (oldData: ClassesDiscountCodeApiResponse | undefined) => {
          if (!oldData) return {
            discountCodes: [discount],
            class: {}
          }
          return {
            ...oldData,
            discountCodes: oldData.discountCodes.map(data =>
              data._id === discount._id ? discount : data,
            )
          }
        },
      )
    },
  })
}


// get all discount codes for the class
const getClassesDiscountCode = async (classId: string) => {
  const response = await axiosGet(`v1/edu-class/${classId}/get-discount-codes`)
  return response.data
}

export const useGetClassesDiscountCode = (classId: string) => {
  const discountCodes = useQuery<ClassesDiscountCodeApiResponse>(
    ['class-discount-code'],
    () => getClassesDiscountCode(classId),
    { enabled: !!classId },
  )

  return discountCodes
}

// get discount code by id
const getClassesDiscountDetailsById = async (discountCodeId: string) => {
  const response = await axiosGet(`v1/edu-class/discount-code/${discountCodeId}`)
  return response.data
}

export const useGetClassesDiscountCodeById = (discountCodeId: string) => {
  const discountById = useQuery<ClassesDiscountCodeResponse>(
    ['class-discount-by-id'],
    () => getClassesDiscountDetailsById(discountCodeId),
    { enabled: false },
  )
  return discountById
}


//Remove Discount Code

const removeClassesDiscountCode = async (discountCodeId: string) => {
  const response = await axiosDeleteWithBody({ status: 'archived' }, `v1/edu-class/discount-code/${discountCodeId}`)
  return response.data
}

export const useRemoveClassesDiscountCode = () => {
  const queryClient = useQueryClient()
  return useMutation(removeClassesDiscountCode, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-discount-code'])
    },
  })
}


//Late fee 
const createClassesLateFee = async ({ payload, classId }: { payload: ClassesLateFee; classId: string }) => {
  const response = await axiosPatch(payload, `v1/edu-class/${classId}/step6-late-fee`)
  return response.data
}

export const useCreateClassesLateFee = () => {
  const queryClient = useQueryClient()
  return useMutation(createClassesLateFee, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-late-fee'])
    },
  })
}

///Drop-in Tickets

const createUpdateClassesStep6DropInTicket = async ({
  payload,
  classId,
  ticketId
}: {
  payload: Step6DropInTicket;
  classId: string;
  ticketId?: string
}) => {
  const url = ticketId
    ? `v1/edu-class/${classId}/step6-dropin-ticket?ticketId=${ticketId}`
    : `v1/edu-class/${classId}/step6-dropin-ticket`

  const response = await axiosPatch(payload, url)
  return response.data
}

export const useCreateUpdateClassesStep6DropInTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(createUpdateClassesStep6DropInTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-dropin-ticket'])
    },
  })
}


//Get Drop-in ticket
const getClassesStep6DropInTicket = async ({ classId }: { classId: string }) => {
  const response = await axiosGet(`v1/edu-class/${classId}/dropin-ticket`)
  return response.data
}

export const useGetClassesStep6DropInTicket = (classId?: string) => {
  const queryClient = useQueryClient()
  return useQuery(['class-dropin-ticket', classId], () => getClassesStep6DropInTicket({ classId: classId || '' }), {
    enabled: !!classId,
  })
}

//Change status of DropIn ticket
const changeDropInTicketStatus = async ({ classId, payload }: { classId: string; payload: DropInTicketStatus }) => {
  const response = await axiosPatch(payload, `v1/edu-class/${classId}/change-status-of-dropin-ticket`)
  return response.data
}

export const useChangeDropInTicketStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(changeDropInTicketStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-dropin-ticket'])
    },
  })
}


//delete drop-in ticket
const deleteClassesStep6DropInTicket = async ({ classId }: { classId: string }) => {
  const response = await axiosDelete(`v1/edu-class/${classId}/delete-dropin-ticket`)
  return response.data
}

export const useDeleteClassesStep6DropInTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteClassesStep6DropInTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-dropin-ticket'])
    },
  })
}


//Delete Class Admission ticket

const deleteClassAdmissionTicket = async ({ classId, ticketId }: { classId: string; ticketId: string }) => {
  const response = await axiosDelete(`v1/edu-class/${classId}/step6-admission-ticket/${ticketId}`)
  return response.data
}

export const useDeleteClassAdmissionTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteClassAdmissionTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-admission-ticket'])
    },
  })
}


//Duplicate Class Admission ticket

const duplicateClassAdmissionTicket = async ({ classId, ticketId }: { classId: string; ticketId: string }) => {
  const response = await axiosPost({}, `v1/edu-class/${classId}/step6-admission-ticket/${ticketId}/duplicate`)
  return response.data
}

export const useDuplicateClassAdmissionTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(duplicateClassAdmissionTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-admission-ticket'])
    },
  })
}


//Delete Additional ticket
const deleteClassAdditionalTicket = async ({ classId, ticketId }: { classId: string; ticketId: string }) => {
  const response = await axiosDelete(`v1/edu-class/${classId}/step6-additional-option/${ticketId}`)
  return response.data
}

export const useDeleteClassAdditionalTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteClassAdditionalTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-additional-ticket'])
    },
  })
}

// Duplicate Additional ticket
const duplicateClassAdditionalTicket = async ({ classId, ticketId }: { classId: string; ticketId: string }) => {
  const response = await axiosPost({}, `v1/edu-class/${classId}/step6-additional-option/${ticketId}/duplicate`)
  return response.data
}

export const useDuplicateClassAdditionalTicket = () => {
  const queryClient = useQueryClient()
  return useMutation(duplicateClassAdditionalTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries(['class-additional-ticket'])
    },
  })
}
